import React, { useState } from 'react';

import { type MessageDescriptor, useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import { Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { useSubscribe } from '@atlaskit/rovo-triggers/main';
import { token } from '@atlaskit/tokens';

type MessageDescriptorWithValues = MessageDescriptor & {
	messageValues?: Record<string, string | number | boolean | null | undefined | Date>;
};

export type GenericActionErrorAction = {
	invocationId: string;
	errors: (string | MessageDescriptorWithValues)[];
};

export const GenericActionError = ({
	errors,
}: {
	errors: (string | MessageDescriptorWithValues)[];
}): React.JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Stack space="space.150">
			{errors.map((error, index) => (
				<Inline space="space.100" alignBlock="center" key={index}>
					<StatusErrorIcon label="Error" color={token('color.icon.danger')} />
					<Text as="p">
						{typeof error === 'string' ? error : formatMessage(error, error.messageValues)}
					</Text>
				</Inline>
			))}
		</Stack>
	);
};

export const WithGenericExternalActionError = ({
	children,
	invocationId,
}: {
	children: React.ReactNode;
	invocationId: string;
}): React.JSX.Element => {
	di(useSubscribe);

	const [errorData, setErrorData] = useState<
		| {
				invocationId: string;
				errors: (string | MessageDescriptorWithValues)[];
		  }
		| undefined
	>();

	useSubscribe({ topic: 'ai-mate' }, (payload) => {
		if (
			payload.type === 'generic-external-action-error' &&
			invocationId === payload.data.invocationId
		) {
			setErrorData(payload.data);
		}
	});

	return (
		<Stack space="space.200">
			{children}
			{errorData && errorData.errors.length > 0 && <GenericActionError errors={errorData.errors} />}
		</Stack>
	);
};
